import React, { Component, Fragment } from "react";
import { connect } from "react-redux";
import { compose } from "redux";
import Col from "react-bootstrap/lib/Col";
import { bool, func, string } from "prop-types";
import { Form, Field, withFormik } from "formik";
import Yup from "yup";
import Button from "pages/_components/Button";
import classNames from "classnames";

import * as i18nUtils from "util/i18n";

import Notification from "pages/_components/Notification";
import { resizableRoute } from "pages/_components/Resizable";
import Container from "pages/_components/Container";
import Head from "pages/_components/Head";
import I18n from "pages/_components/I18n";
import MainContainer from "pages/_components/MainContainer";
import {
    actions as frequentDestinationActions,
    selectors as frequentDestinationSelector,
} from "reducers/frequentDestination";
import Credential from "pages/_components/fields/credentials/Credential";
import Heading from "pages/_components/Heading";
import isTokenActive from "util/token";
import { AgendaConfirm } from "@ui-kit/components/index.es";
import {
    dispatchV3ShellLayout,
    isUxDashboardImprovementsEnabled,
    isContactListUxEnabled,
} from "util/v3ShellLayoutSync";
import { actions as layoutActions } from "reducers/layout";
import { selectors as statusSelectors } from "reducers/status";
import { actions as tokenActions, selectors as tokenSelectors } from "reducers/token";
import { resolveFrequentDestinationGoBackPath } from "pages/frequentDestinations/_utils/resolveGoBackPath";
import {
    buildFrequentDestinationConfirmFields,
    buildAgendaSoftTokenConfirmProps,
    otpFormikBag,
} from "pages/frequentDestinations/_utils/frequentDestinationSummary";
import { goBack } from "react-router-redux";

const FORM_ID = "modifyFrequentDestinations";
class ModifyFrequentDestinationConfirmations extends Component {
    static propTypes = {
        fetching: bool.isRequired,
        frequentDestination: {},
        dispatch: func.isRequired,
        isDesktop: bool.isRequired,
        name: string.isRequired,
        email: string.isRequired,
        idFrequentDestination: string.isRequired,
        lastHrefPathname: string,
        tokenNumber: string,
    };

    static defaultProps = {
        frequentDestination: {},
        lastHrefPathname: "",
        tokenNumber: null,
    };

    componentDidMount = () => {
        const { dispatch, idFrequentDestination, frequentDestination, isDesktop, lastHrefPathname } = this.props;

        // Only load when the modify draft is missing (e.g. hard refresh). Re-fetching here would
        // overwrite preview edits (email/reference) that the form must restore on history back.
        if (!frequentDestination?.idFrequentDestination && idFrequentDestination) {
            dispatch(frequentDestinationActions.modifyFrequentDestinationPreRequest(idFrequentDestination));
        }
        if (!isDesktop && isTokenActive()) {
            dispatch(tokenActions.softTokenGenerationRequest());
        }
        dispatchV3ShellLayout(dispatch, {
            titleKey: "frequentDestination.modify.tittle",
            // Fallback if handler is cleared; prefer history back (see setGoBackHandler below).
            goBackPath: resolveFrequentDestinationGoBackPath(lastHrefPathname),
            rightContentMobile: null,
            scopes: isContactListUxEnabled()
                ? ["frequentDestination", "frequentDestinationModifyConfirm", "agenda"]
                : ["frequentDestination"],
        });
        // Mobile V3 shell uses goBackPath (AgendaConfirm hides PageHeader on mobile).
        // Mirror desktop PageHeader goBack() so confirm → back returns to the modify form.
        dispatch(layoutActions.setGoBackHandler(this.handleBack));
    };

    componentWillUnmount() {
        const { dispatch, lastHrefPathname } = this.props;
        dispatch(layoutActions.setGoBackHandler(null));
        dispatchV3ShellLayout(dispatch, {
            titleKey: "",
            goBackPath: resolveFrequentDestinationGoBackPath(lastHrefPathname),
            rightContentMobile: null,
            scopes: null,
        });
    }

    handleBack = () => {
        const { dispatch } = this.props;
        dispatch(goBack());
    };

    centerContentMobile = () => (
        <div className="title-account-header-multiline-background-blue">
            <h1>{i18nUtils.get("frequentDestination.modify.tittle")}</h1>
        </div>
    );

    renderV3 = () => {
        const { fetching, frequentDestination, name, email, dispatch, tokenNumber, isDesktop } = this.props;
        const softTokenProps = buildAgendaSoftTokenConfirmProps(tokenNumber, isDesktop);
        return (
            <AgendaConfirm
                key={softTokenProps.confirmKey}
                styleV3
                title={i18nUtils.get("frequentDestination.modify.tittle")}
                goBack={this.handleBack}
                fields={buildFrequentDestinationConfirmFields(frequentDestination, { reference: name, email })}
                loading={fetching}
                getToken={softTokenProps.getToken}
                confirmationText={softTokenProps.confirmationText}
                validationConfirm={softTokenProps.validationConfirm}
                onConfirm={(values) => {
                    dispatch(
                        frequentDestinationActions.modifyFrequentDestinationRequest(
                            frequentDestination.idFrequentDestination,
                            name,
                            email,
                            softTokenProps.resolveOtp(values),
                            otpFormikBag(dispatch, ["frequentDestinationModifyConfirm", "agenda"]),
                        ),
                    );
                }}
            />
        );
    };

    render() {
        if (isContactListUxEnabled()) {
            const uxDashboard = isUxDashboardImprovementsEnabled();
            return (
                <>
                    {!uxDashboard ? <Notification scopeToShow="frequentDestinationModifyConfirm" /> : null}
                    {this.renderV3()}
                </>
            );
        }
        const { fetching, frequentDestination, name, email, isDesktop } = this.props;
        const uxDashboard = isUxDashboardImprovementsEnabled();
        const containerClassName = `transfer-data data-wrapper-flex ${
            !isDesktop ? "space-between mb-0 container-details" : ""
        }`;
        const labelClassName = isDesktop ? "mb-0" : "";
        return (
            <Fragment>
                {!uxDashboard ? <Notification scopeToShow="frequentDestinationModifyConfirm" /> : null}
                <div className="admin-detail-head px-0">
                    {(!uxDashboard || isDesktop) && (
                        <Head
                            onBack={this.handleBack}
                            closeLinkTo="/v2/agenda"
                            centerElement={isDesktop ? null : this.centerContentMobile}
                            headerClassName={!isDesktop ? "blue-main-header-mobile" : ""}
                            emptyRightOption
                        />
                    )}
                    {!uxDashboard ||
                        (isDesktop && (
                            <div className="view-title">
                                <h1>{i18nUtils.get("frequentDestination.modify.tittle")}</h1>
                            </div>
                        ))}
                </div>

                {frequentDestination && (
                    <MainContainer showLoader={fetching}>
                        <Form className={!isDesktop ? "above-the-fold" : "confirmation__container"}>
                            <Container
                                className={!isDesktop && "align-items-center flex-grow container-white with-margin"}
                                gridClassName={!isDesktop ? "form-content" : "confirmation__form confirmation__box"}>
                                <div className={classNames("admin-content-center", { "mb-3": !isDesktop })}>
                                    <I18n
                                        id="frequentDestination.modify.sustitle"
                                        component={!isDesktop ? "h2" : "h1"}
                                        componentProps={!isDesktop && { className: "font-size-20-px mx-3" }}
                                    />
                                </div>
                                <Col sm={6} lg={12} className="col col-12">
                                    <Heading.DataGroup
                                        containerClassName={containerClassName}
                                        labelClassName={labelClassName}
                                        label="frequentDestination.create.confirmate.numberAccount"
                                        data={`${frequentDestination.accountNumber}`}
                                    />
                                    <Heading.DataGroup
                                        containerClassName={containerClassName}
                                        labelClassName={labelClassName}
                                        label="frequentDestination.create.confirmate.accountType"
                                        data={`${frequentDestination.productType}`}
                                    />
                                    <Heading.DataGroup
                                        containerClassName={containerClassName}
                                        labelClassName={labelClassName}
                                        label="frequentDestination.create.confirmate.own"
                                        data={`${frequentDestination.isOwn === "1" ? "SI" : "NO"}`}
                                    />
                                    <Heading.DataGroup
                                        containerClassName={containerClassName}
                                        labelClassName={labelClassName}
                                        label="frequentDestination.create.confirmate.cbu"
                                        data={`${frequentDestination.cbu}`}
                                    />
                                    <Heading.DataGroup
                                        containerClassName={containerClassName}
                                        labelClassName={labelClassName}
                                        label="frequentDestination.create.confirmate.document"
                                        data={`${frequentDestination.recipientDocumentType}
                                            ${frequentDestination.recipientDocumentNumber}`}
                                    />
                                    <Heading.DataGroup
                                        containerClassName={containerClassName}
                                        labelClassName={labelClassName}
                                        label="frequentDestination.create.confirmate.recipientName"
                                        data={`${frequentDestination.recipientName}`}
                                    />
                                    <Heading.DataGroup
                                        containerClassName={containerClassName}
                                        labelClassName={labelClassName}
                                        label={`frequentDestination.create.confirmate.${
                                            frequentDestination.cbu?.startsWith("000") ? "entity" : "bank"
                                        }`}
                                        data={`${frequentDestination.localBank}/${frequentDestination.subsidiaryBank}`}
                                    />
                                    <Heading.DataGroup
                                        containerClassName={containerClassName}
                                        labelClassName={labelClassName}
                                        label="frequentDestination.create.confirmate.email"
                                        data={email}
                                    />
                                    <Heading.DataGroup
                                        containerClassName={containerClassName}
                                        labelClassName={labelClassName}
                                        label="frequentDestination.create.confirmate.referent"
                                        data={`${name}`}
                                    />

                                    <div className="admin-content-center mt-5">
                                        {!isTokenActive(!isDesktop) ? (
                                            <I18n
                                                id="transfer.summary.confirm.label"
                                                component="p"
                                                componentProps={{
                                                    className: "account-dropdown-header-font font-light px-3",
                                                }}
                                            />
                                        ) : (
                                            <div className="admin-content-center pb-3">
                                                <I18n
                                                    id="confirmation.withoutotp.text"
                                                    component="p"
                                                    componentProps={{ className: "confirmation__text-without-otp" }}
                                                />
                                            </div>
                                        )}
                                    </div>
                                </Col>
                                <Col sm={12} className="col col-12 d-flex justify-content-center">
                                    <Col sm={12} md={6} lg={12} className="col col-12 px-0">
                                        <Field
                                            placeholderText={i18nUtils.get(`confirmation.otp.placeholder`)}
                                            name="otp"
                                            type="otp"
                                            idForm={FORM_ID}
                                            component={Credential}
                                        />
                                    </Col>
                                </Col>
                                <Container className="flex-grow align-items-center w-100" gridClassName="form-content">
                                    <Col lg={12} md={12} sm={12} className="px-0">
                                        <Col className="confirmation__content-button" lg={12} md={12} sm={12}>
                                            <div className="admin-content-center">
                                                <Button
                                                    type="submit"
                                                    bsStyle="primary"
                                                    label="global.confirm"
                                                    loading={fetching}
                                                    image="images/arrowRight.svg"
                                                    imageStyle={isDesktop ? "mr-2" : "mr-1"}
                                                    className="text-capitalize"
                                                />
                                            </div>
                                        </Col>
                                    </Col>
                                </Container>
                            </Container>
                        </Form>
                    </MainContainer>
                )}
            </Fragment>
        );
    }
}

const mapStateToProps = (state) => ({
    frequentDestination: frequentDestinationSelector.getFrequentDestinationModify(state),
    name: frequentDestinationSelector.getReference(state),
    email: frequentDestinationSelector.getEmail(state),
    fetching: frequentDestinationSelector.getFetching(state),
    idFrequentDestination: frequentDestinationSelector.getIdDestinationModify(state),
    lastHrefPathname: statusSelectors.getPathname(state) || "",
    tokenNumber: tokenSelectors.getTokenNumber(state),
});

export default compose(
    connect(mapStateToProps),
    resizableRoute,
    withFormik({
        mapPropsToValues: () => ({ otp: "" }),
        validationSchema: () =>
            Yup.lazy(() =>
                Yup.object().shape({
                    otp: Yup.string().required(i18nUtils.get("accounts.new.account.otp.error.required")),
                }),
            ),
        handleSubmit: ({ otp }, formikBag) => {
            const { dispatch, name, email, frequentDestination } = formikBag.props;
            dispatch(
                frequentDestinationActions.modifyFrequentDestinationRequest(
                    frequentDestination.idFrequentDestination,
                    name,
                    email,
                    otp,
                    formikBag,
                ),
            );
        },
    }),
)(ModifyFrequentDestinationConfirmations);
